import { useEffect, useState } from "react";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { DollarSign } from "lucide-react";
import api from "@/lib/api";
import { money, groupByWeek, groupByMonth } from "@/lib/analytics";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Costs() {
  const [orders, setOrders] = useState([]);
  const [workerPay, setWorkerPay] = useState(0);

  useEffect(() => { (async () => { try { const { data } = await api.get("/analytics/costs"); setOrders(data.orders || []); } catch { /* */ } })(); }, []);
  useEffect(() => { (async () => {
    try {
      const start = format(startOfMonth(new Date()), "yyyy-MM-dd");
      const end = format(endOfMonth(new Date()), "yyyy-MM-dd");
      const { data } = await api.get("/staff/pay-preview", { params: { start, end } });
      setWorkerPay(data.total_pay || 0);
    } catch { /* */ }
  })(); }, []);

  const orderTotal = orders.reduce((s, o) => s + Number(o.cost || 0), 0);
  const total = orderTotal + workerPay;
  const weekly = groupByWeek(orders, "date", "cost");
  const monthly = groupByMonth(orders, "date", "cost");

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold tracking-tight">Cost Analytics</h1>
      <p className="mt-1 text-sm text-muted-foreground">Order costs plus this month's worker pay.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total Cost" value={money(total)} icon={DollarSign} />
        <Stat label="Order Costs" value={money(orderTotal)} icon={DollarSign} />
        <Stat label="Worker Pay (This Month)" value={money(workerPay)} icon={DollarSign} testId="cost-worker-pay" />
        <Stat label="Orders" value={orders.length} icon={DollarSign} />
      </div>

      <div className="mt-6 rounded-xl border border-border/60 bg-card p-5">
        <h3 className="mb-4 font-heading font-bold">Costs Over Time</h3>
        <Tabs defaultValue="weekly">
          <TabsList className="mb-4"><TabsTrigger value="weekly" data-testid="cost-tab-weekly">Weekly</TabsTrigger><TabsTrigger value="monthly" data-testid="cost-tab-monthly">Monthly</TabsTrigger></TabsList>
          <TabsContent value="weekly"><Chart data={weekly} /></TabsContent>
          <TabsContent value="monthly"><Chart data={monthly} /></TabsContent>
        </Tabs>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Medication</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Units</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
          <TableBody>
            {orders.length === 0 ? <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No cost data yet.</TableCell></TableRow>
              : orders.map((o) => (
                <TableRow key={o.id} data-testid={`cost-row-${o.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{format(new Date(o.date), "MMM d, yyyy")}</TableCell>
                  <TableCell className="font-medium">{o.medication_name}</TableCell>
                  <TableCell className="text-right">{money(o.cost)}</TableCell>
                  <TableCell className="text-right">{o.units}</TableCell>
                  <TableCell>{o.bought_from}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, testId }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-5" data-testid={testId}>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary"><Icon className="h-4 w-4" /></div>
      <p className="mt-3 font-heading text-2xl font-extrabold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function Chart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="label" /><YAxis /><Tooltip formatter={(v) => money(v)} />
        <Bar dataKey="value" name="Cost" fill="hsl(24 90% 55%)" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
